
import { useState, useEffect, useCallback, useRef } from 'react';
import { AppUser, LiveLocation, LiveViewer } from '../types';
import { useLocationGate } from '../contexts/LocationGateContext';
import {
  startSharingLocation,
  stopSharingLocation,
  subscribeToGroupLocations,
  setViewerPresence,
  clearViewerPresence,
  subscribeToGroupViewers,
} from '../services/locationSharingService';

export function useLiveLocations(user: AppUser | null) {
  const { gatedWatchPosition } = useLocationGate();
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [nearbyUsers, setNearbyUsers] = useState<LiveLocation[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [activeGroupName, setActiveGroupName] = useState<string | null>(null);
  const [groupViewers, setGroupViewers] = useState<LiveViewer[]>([]);
  const activeGroupIdRef = useRef<string | null>(null);

  // Keep ref in sync for cleanup
  useEffect(() => { activeGroupIdRef.current = activeGroupId; }, [activeGroupId]);

  // Subscribe to group positions when a group is active
  useEffect(() => {
    if (!user || !activeGroupId) {
      setNearbyUsers([]);
      return;
    }
    const unsub = subscribeToGroupLocations(activeGroupId, user.uid, setNearbyUsers);
    return () => unsub();
  }, [user?.uid, activeGroupId]);

  // Subscribe to group viewers when a group is active
  useEffect(() => {
    if (!user || !activeGroupId) {
      setGroupViewers([]);
      return;
    }
    const unsub = subscribeToGroupViewers(activeGroupId, setGroupViewers);
    return () => unsub();
  }, [user?.uid, activeGroupId]);

  // Viewer presence lifecycle: set on focus, clear on blur, heartbeat every 60s
  useEffect(() => {
    if (!user || !activeGroupId) return;

    const setPresence = () =>
      setViewerPresence(activeGroupId, user.uid, user.displayName || 'Ukjent', user.photoURL || undefined);
    const clearPresence = () =>
      clearViewerPresence(activeGroupId, user.uid);

    setPresence();
    const interval = setInterval(setPresence, 60_000);

    const onVisChange = () => {
      if (document.hidden) clearPresence();
      else setPresence();
    };
    document.addEventListener('visibilitychange', onVisChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisChange);
      clearPresence();
    };
  }, [user?.uid, activeGroupId]);

  const startSharing = useCallback(async (groupId: string, groupName: string) => {
    if (!user) { setShareError('Du må være innlogget for å dele posisjon.'); return; }
    setShareError(null);
    setIsSharing(true);
    setActiveGroupId(groupId);
    setActiveGroupName(groupName);
    try {
      await startSharingLocation(
        user.uid,
        user.displayName || 'Ukjent',
        user.photoURL || undefined,
        groupId,
        groupName,
        gatedWatchPosition,
      );
    } catch (e: any) {
      setIsSharing(false);
      setActiveGroupId(null);
      setActiveGroupName(null);
      const msg = e?.code === 1
        ? 'GPS-tilgang nektet. Tillat posisjon i nettleser-innstillinger.'
        : 'Kunne ikke starte posisjonsdeling.';
      setShareError(msg);
    }
  }, [user]);

  const stopSharing = useCallback(async () => {
    if (!user || !activeGroupIdRef.current) return;
    await stopSharingLocation(user.uid, activeGroupIdRef.current);
    setIsSharing(false);
    setActiveGroupId(null);
    setActiveGroupName(null);
  }, [user?.uid]);

  // Auto-stop when user logs out
  useEffect(() => {
    if (!user && isSharing) {
      setIsSharing(false);
      setActiveGroupId(null);
      setActiveGroupName(null);
    }
  }, [user, isSharing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (user && activeGroupIdRef.current) {
        stopSharingLocation(user.uid, activeGroupIdRef.current).catch(() => {});
      }
    };
  }, []);

  return {
    isSharing,
    shareError,
    nearbyUsers,
    startSharing,
    stopSharing,
    activeGroupId,
    activeGroupName,
    groupViewers,
  };
}
